import { useEffect, useMemo, useState, type CSSProperties, type ChangeEvent } from 'react'
import { generateBrandedQrSvg } from '../lib/qrBrand'
import type { TenantSession } from '../lib/session'
import {
  listValidations, CONSUMER_URL,
  updateBranding, uploadLogo, deleteLogo,
  type MeResponse, type TenantInfo, type Validation,
} from '../lib/api'
import {
  DEFAULT_ADJUST, detectBrandColor, loadLogoSource, loadLogoSourceFromUrl,
  type LogoAdjust, type LogoSource,
} from '@parkify/ui/logo-brand'
import LogoAdjuster from '@parkify/ui/logo-adjuster'
import { validationDisplayLabel } from './Validate'
import { formatLotValidationHours } from '../lib/validationHours'
import { deliveryMethodLabel } from '../lib/deliveryMethod'
import { labelStyle, purpleBtn, screenTitle, buttonRadius } from '../ui'

const inputStyle: CSSProperties = {
  width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--color-gray-300)',
  fontFamily: 'inherit', fontSize: 13, background: '#fff',
}
const VALIDATIONS_GRID_COLUMNS = '130px minmax(180px, 1fr) 120px 110px 120px 90px 64px'

/**
 * The program card's window, labelled in the LOT's clock (demo #89): the
 * `activeHours` values are lot wall-clock times and `timezone` is the server's
 * anchor for them, so a manager in another timezone does not read them as
 * their own. Same formatter the Validation screen uses.
 */
export function programHoursLabel(v: Pick<Validation, 'activeHours' | 'timezone'>, at = new Date()): string | null {
  if (!v.activeHours) return null
  return `Available: ${formatLotValidationHours(v.activeHours, v.timezone, at)}`
}

export function Dashboard({ session, me, hideMonetaryValues = false, canManageTenant = true }: {
  session: TenantSession
  me: MeResponse
  hideMonetaryValues?: boolean
  canManageTenant?: boolean
}) {
  const [list, setList] = useState<Validation[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [qrFor, setQrFor] = useState<Validation | null>(null)

  const load = async () => {
    setLoading(true); setErr('')
    try { setList((await listValidations(session.token)).sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''))) }
    catch (e) { setErr(e instanceof Error ? e.message : 'load failed'); console.error('[tenant] load', e) }
    finally { setLoading(false) }
  }
  useEffect(() => { void load() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  /*
    READ-ONLY BY DESIGN. A tenant views its programs, downloads their QR codes
    and printable cards, and issues them from the Validation screen. It never
    creates, edits or deletes one: the terms (amount, caps, expiry, zones) are
    set by the operator in the console, and the API refuses a tenant write
    (tenant_users/access.ts, and the backstop on the /api/validations routes).
    Maecy, #dev-requests 2026-09-25: a tenant could change or remove its own
    program from here.
  */
  return (
    <div>
      {canManageTenant && <BrandingCard session={session} tenant={me.tenant} />}

      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 12, rowGap: 4, marginBottom: 14 }}>
        <h2 style={{ ...screenTitle, whiteSpace: 'nowrap' }}>My validations</h2>
        <span style={{ fontSize: 12, color: 'var(--color-gray-600)', whiteSpace: 'nowrap' }}>{loading ? 'loading…' : err ? '' : `${list.length} total`}</span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: 'var(--color-gray-600)' }}>Your Parkify contact sets these up and changes them for you.</span>
      </div>

      {/*
        Stated once, and stated honestly — which as of #1734 means CONDITIONALLY.

        #1730 asserted the first sentence as a fixed string, and was right to at
        the time: nothing was on a schedule, so the portal must not imply a
        cadence it could not deliver. #1734 delivered one, which inverts the
        hazard — the same fixed sentence now DENIES a schedule the tenant is
        actually on, on the exact screen they read before ordering signage. The
        failure is worse in this direction: over-promising made the portal look
        vague, but denying a real schedule makes a tenant confidently print a
        code with a known death date.

        So it's derived from the list. Either way the second sentence holds, and
        it is the load-bearing promise: a replaced code shows its predecessor and
        that predecessor's cut-off date on its row (RotationNote).
      */}
      <div style={{ fontSize: 12, color: 'var(--color-gray-600)', background: 'var(--color-gray-100)', border: '1px solid var(--color-gray-200)', borderRadius: 10, padding: '10px 14px', marginBottom: 12, lineHeight: 1.5 }}>
        {list.some(v => v.rotation)
          ? <>Some of your codes change on a schedule — each one’s next change date is on its QR download, so you can print to it.</>
          : <>Your codes don’t rotate on a schedule today.</>}
        {' '}If one is ever replaced, the new code — and the date the old one stops working — appears here.
      </div>

      {canManageTenant && <RotationContactCard session={session} tenant={me.tenant} />}

      <div style={{ background: '#fff', borderRadius: 12, overflowX: 'auto', border: '1px solid var(--color-gray-200)' }}>
        <div style={{ display: 'grid', width: '100%', gridTemplateColumns: VALIDATIONS_GRID_COLUMNS, padding: '10px 16px', borderBottom: '1px solid var(--color-gray-200)', fontSize: 11, fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--color-gray-600)' }}>
          <div>Code</div><div>Offer</div><div>{hideMonetaryValues ? 'Parking' : 'Discount'}</div><div>Remaining</div><div>Delivery</div><div>Status</div><div></div>
        </div>
        {list.length === 0 ? (
          err ? (
            <div style={{ padding: 16 }}><LoadError message={err} onRetry={() => void load()} /></div>
          ) : (
            <div style={{ padding: 32, textAlign: 'center', fontSize: 13, color: 'var(--color-gray-600)' }}>
              {loading ? '' : 'No validations yet. Your Parkify contact sets these up.'}
            </div>
          )
        ) : list.map(v => (
          <div key={v.code} style={{ display: 'grid', width: '100%', gridTemplateColumns: VALIDATIONS_GRID_COLUMNS, alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--color-gray-100)', fontSize: 13 }}>
            <code style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, background: 'var(--color-gray-100)', padding: '3px 8px', borderRadius: 6, width: 'fit-content' }}>{v.code}</code>
            <div style={{ fontWeight: 600, paddingRight: 8 }}>
              {v.name || '—'}
              {v.activeHours && <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--color-gray-600)' }}>{programHoursLabel(v)}</div>}
              {v.retiredCodes && v.retiredCodes.length > 0 && <RotationNote retired={v.retiredCodes} />}
              {v.expiresAt && (
                <div style={{ fontSize: 11, fontWeight: 500, color: isExpiredIso(v.expiresAt) ? '#b91c1c' : 'var(--color-gray-600)' }}>
                  {isExpiredIso(v.expiresAt) ? 'Expired' : 'Expires'} {shortDate(v.expiresAt)}
                </div>
              )}
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 12 }}>
              {hideMonetaryValues
                ? validationDisplayLabel(v, true)
                : v.type === 'full' ? '100%' : v.type === 'flat' ? `$${(v.value / 100).toFixed(2)}` : v.type === 'percent' ? `${v.value}%` : v.type === 'until' ? `→ ${v.untilTime ?? '—'}` : `${v.value} min`}
            </div>
            <div style={{ fontSize: 12 }}>{v.inventory ? `${v.remaining ?? '—'} / ${v.inventory.cap}` : <span style={{ color: 'var(--color-gray-600)' }}>Unlimited</span>}</div>
            <div style={{ fontSize: 12, paddingRight: 8 }}>{deliveryMethodLabel(v.deliveryMethod)}</div>
            <div>{v.active === false
              ? <span style={pill('var(--color-gray-200)', 'var(--color-gray-600)')}>Off</span>
              : isExpiredIso(v.expiresAt) ? <span style={pill('rgba(237,28,36,0.12)', '#b91c1c')}>Expired</span>
              : <span style={pill('rgba(98,197,132,0.15)', '#15803d')}>Active</span>}</div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={() => setQrFor(v)} title="QR code" aria-label={`QR code for ${v.code}`} style={iconBtn()}>▦</button>
              <button onClick={() => downloadCodeCard(v, me.tenant.name)} title="Download a printable card with this code" aria-label={`Download card for ${v.code}`} style={iconBtn()}>⤓</button>
            </div>
          </div>
        ))}
      </div>

      {qrFor && <QrModal validation={qrFor} zones={me.zones} hideMonetaryValues={hideMonetaryValues} onClose={() => setQrFor(null)} />}
    </div>
  )
}

function QrModal({ validation, zones, hideMonetaryValues, onClose }: { validation: Validation; zones: { id: string; displayName: string }[]; hideMonetaryValues: boolean; onClose: () => void }) {
  const targets = useMemo(() => {
    if (validation.zones.length === 0) return zones
    const set = new Set(validation.zones)
    return zones.filter(z => set.has(z.id))
  }, [validation, zones])
  const [svgs, setSvgs] = useState<Record<string, string>>({})

  useEffect(() => {
    void (async () => {
      const out: Record<string, string> = {}
      for (const z of targets) {
        const url = `${CONSUMER_URL}/?z=${encodeURIComponent(z.id)}&code=${encodeURIComponent(validation.code)}`
        out[z.id] = await generateBrandedQrSvg(url)
      }
      setSvgs(out)
    })()
  }, [targets, validation.code])

  const download = (zoneId: string) => {
    const svg = svgs[zoneId]; if (!svg) return
    const a = document.createElement('a')
    a.href = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }))
    a.download = `parkify-${validation.code}-${zoneId}.svg`; a.click(); URL.revokeObjectURL(a.href)
  }

  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={e => e.stopPropagation()} style={{ ...modal, maxWidth: 'min(720px, 94vw)' }}>
        <h3 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 4px' }}>QR codes — {validation.name || validation.code}</h3>
        <p style={{ fontSize: 12, color: 'var(--color-gray-600)', margin: '0 0 16px' }}>
          {hideMonetaryValues ? 'Scanning opens parking with this validation applied.' : 'Scanning opens checkout with this discount pre-applied.'} One per lot.
        </p>
        {/*
          The shelf life of the artwork about to be downloaded, in one block.

          #1733 put the retired-code note here — the BACKWARD half, and the right
          instinct: this is the moment a reprint deadline is actionable. But
          `retiredCodes` only ever describes a rotation that ALREADY happened,
          which by definition arrives after the signage was printed. The tenant
          committing money to a print run needs the FORWARD half: the date this
          code dies. That's `rotation.nextAt`, and it was reaching ops but not
          the party who actually pays the printer.

          Both halves in ONE box, not two stacked ones — a tenant reading two
          notices about the same code's lifetime has to work out whether they
          disagree, and "how long is this good for" deserves a single answer.
        */}
        {(validation.rotation || (validation.retiredCodes?.length ?? 0) > 0) && (
          <div style={{
            borderRadius: 10, padding: '8px 12px', margin: '-8px 0 16px',
            // Amber is reserved for the one state with an action attached: an old
            // code still live, i.e. reprint before it lapses. A schedule on its
            // own is a fact about the future, not a warning.
            background: hasLiveRetired(validation) ? 'rgba(245,158,11,0.10)' : 'var(--color-gray-50, #fafafa)',
            border: `1px solid ${hasLiveRetired(validation) ? 'rgba(245,158,11,0.35)' : 'var(--color-gray-200)'}`,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-gray-700)' }}>
              These QR codes carry <code style={{ fontFamily: 'monospace' }}>{validation.code}</code>.
            </div>
            {validation.rotation && (
              <div style={{ marginTop: 3, fontSize: 11, color: 'var(--color-gray-600)' }}>
                It changes on <strong>{shortDate(validation.rotation.nextAt)}</strong> — print only what you'll
                use by then. We'll email the new code when it changes
                {validation.rotation.overlapDays !== 0 && <>, and this one keeps working while you reprint</>}.
              </div>
            )}
            {validation.retiredCodes && validation.retiredCodes.length > 0 && (
              <RotationNote retired={validation.retiredCodes} />
            )}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
          {targets.length === 0 && <div style={{ fontSize: 13, color: 'var(--color-gray-600)' }}>No lots assigned to this validation.</div>}
          {targets.map(z => (
            <div key={z.id} style={{ border: '1px solid var(--color-gray-200)', borderRadius: 12, padding: 16, textAlign: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>{z.displayName}</div>
              <div style={{ width: '100%', maxWidth: 180, margin: '0 auto', aspectRatio: '1/1' }} dangerouslySetInnerHTML={{ __html: svgs[z.id] ?? '' }} />
              <button onClick={() => download(z.id)} style={{ ...purpleBtn, background: 'transparent', color: 'var(--color-gray-700)', border: '1.5px solid var(--color-gray-300)', fontSize: 11, marginTop: 10 }}>Download SVG</button>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <button onClick={onClose} style={purpleBtn}>Done</button>
        </div>
      </div>
    </div>
  )
}

// Tenant self-serve branding — the logo + brand color shown to parkers who
// scan this tenant's validation (consumer checkout hero + soft page tint).
function BrandingCard({ session, tenant }: { session: TenantSession; tenant: TenantInfo }) {
  const [brandColor, setBrandColor] = useState(tenant.brandColor ?? '')
  const [logoBlob, setLogoBlob] = useState<Blob | null>(null)
  const [preview, setPreview] = useState<string | null>(tenant.logoUrl ?? null)
  const [busy, setBusy] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [savedAt, setSavedAt] = useState(0)
  // Committed baseline — what was last persisted. dirty compares the working
  // state against THIS, not the `tenant` prop (never mutate props).
  const [baseColor, setBaseColor] = useState(tenant.brandColor ?? '')
  const [baseLogo, setBaseLogo] = useState<string | undefined>(tenant.logoUrl)
  // The decoded source + framing behind the Adjust dialog. Kept after Apply so
  // "Adjust" reopens on the same bytes, where they left off.
  const [source, setSource] = useState<LogoSource | null>(null)
  const [adjust, setAdjust] = useState<LogoAdjust>(DEFAULT_ADJUST)
  const [adjusting, setAdjusting] = useState(false)

  const validHex = /^#[0-9a-fA-F]{6}$/.test(brandColor)
  const removed = !!baseLogo && preview === null && !logoBlob
  const dirty = (brandColor.trim() || '') !== baseColor || !!logoBlob || removed

  // Decode the chosen file, auto-fill the (editable) brand color from the whole
  // logo, then open the framing dialog. Nothing is staged for upload until they
  // apply a framing — that step is what produces the square PNG.
  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setProcessing(true)
    try {
      const src = await loadLogoSource(file)
      setSource(src)
      setAdjust(DEFAULT_ADJUST)
      setBrandColor(detectBrandColor(src))
      setAdjusting(true)
    } catch (err) {
      console.error('[tenant] logo process', err)
      alert('Could not read that image. Try a PNG, JPG, WebP, or SVG.')
    } finally { setProcessing(false) }
  }

  // Reframe without re-picking a file: reuse this session's decoded source, or
  // re-fetch the saved logo from the serve route (CORS-open, canvas-safe).
  const onAdjust = async () => {
    if (source) { setAdjusting(true); return }
    if (!baseLogo) return
    setProcessing(true)
    try {
      const src = await loadLogoSourceFromUrl(baseLogo)
      setSource(src)
      setAdjust(DEFAULT_ADJUST)
      setAdjusting(true)
    } catch (err) {
      console.error('[tenant] logo reload', err)
      alert("Couldn't reopen your saved logo — upload the image again to adjust it.")
    } finally { setProcessing(false) }
  }

  const save = async () => {
    if (busy || !dirty) return
    if (brandColor.trim() && !validHex) { alert('Brand color must be a 6-digit hex, e.g. #7F3F98'); return }
    setBusy(true)
    try {
      await updateBranding(session.token, { brandColor: brandColor.trim() || '' })
      if (logoBlob) {
        const { logoUrl } = await uploadLogo(session.token, logoBlob)
        setPreview(logoUrl); setLogoBlob(null); setBaseLogo(logoUrl)
      } else if (removed) {
        await deleteLogo(session.token)
        setBaseLogo(undefined)
      }
      setBaseColor(brandColor.trim() || '')
      setSavedAt(Date.now())
    } catch (e) {
      alert(`Save failed: ${e instanceof Error ? e.message : 'error'}`)
    } finally { setBusy(false) }
  }

  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--color-gray-200)', padding: 18, marginBottom: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 2 }}>Branding</div>
      <div style={{ fontSize: 12, color: 'var(--color-gray-600)', marginBottom: 14, lineHeight: 1.4 }}>
        Parkers who scan your validation see your logo and brand color leading the checkout page.
      </div>
      <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Logo */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
          <div style={{
            width: 76, height: 76, borderRadius: 12, border: '1.5px dashed var(--color-gray-300)',
            background: preview ? '#fff' : 'var(--color-gray-100)', overflow: 'hidden',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {preview
              ? <img src={preview} alt="Logo preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', padding: 6 }} />
              : <span style={{ fontSize: 10, color: 'var(--color-gray-600)' }}>No logo</span>}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <label style={{ ...miniBtn(), opacity: processing ? 0.5 : 1 }}>
              {processing ? '…' : preview ? 'Replace' : 'Upload'}
              <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" onChange={onFile} disabled={processing} style={{ display: 'none' }} />
            </label>
            {preview && <button type="button" onClick={onAdjust} disabled={processing} style={miniBtn()}>Adjust</button>}
            {preview && <button type="button" onClick={() => { setLogoBlob(null); setPreview(null); setSource(null) }} style={miniBtn(true)}>Remove</button>}
          </div>
        </div>
        {/* Brand color */}
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={labelStyle}>Brand color</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="color" value={validHex ? brandColor : '#7F3F98'} onChange={e => setBrandColor(e.target.value)}
              style={{ width: 42, height: 36, padding: 0, border: '1.5px solid var(--color-gray-300)', borderRadius: 8, background: 'none', cursor: 'pointer' }} />
            <input value={brandColor} onChange={e => setBrandColor(e.target.value)} placeholder="#7F3F98"
              style={{ ...inputStyle, fontFamily: 'monospace', textTransform: 'uppercase', width: 120, flex: '0 0 auto' }} />
            {brandColor && <button type="button" onClick={() => setBrandColor('')} style={{ background: 'none', border: 'none', color: 'var(--color-gray-600)', fontSize: 12, textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit' }}>Clear</button>}
          </div>
          <div style={{ fontSize: 11, color: 'var(--color-gray-600)', marginTop: 6 }}>Auto-detected from your logo — adjust if needed.</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
        <button onClick={save} disabled={busy || !dirty} style={{ ...purpleBtn, opacity: busy || !dirty ? 0.5 : 1 }}>
          {busy ? 'Saving…' : 'Save branding'}
        </button>
        {savedAt > 0 && !dirty && <span style={{ fontSize: 12, color: '#15803d', fontWeight: 600 }}>✓ Saved</span>}
      </div>

      {/* Framing dialog. Applying stages the square PNG; it uploads on Save. */}
      {adjusting && source && (
        <LogoAdjuster
          source={source}
          initial={adjust}
          accent={validHex ? brandColor : undefined}
          onCancel={() => setAdjusting(false)}
          onApply={({ blob, dataUrl, adjust: next }) => {
            setLogoBlob(blob)
            setPreview(dataUrl)
            setAdjust(next)
            setAdjusting(false)
          }}
        />
      )}
    </div>
  )
}

function miniBtn(danger = false): CSSProperties {
  return { padding: '5px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', border: '1.5px solid var(--color-gray-300)', background: 'transparent', color: danger ? '#b91c1c' : 'var(--color-gray-700)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }
}

const overlay: CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(35,31,32,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16 }
const modal: CSSProperties = { width: 'min(560px, 94vw)', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }
function pill(bg: string, color: string): CSSProperties { return { fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 10, background: bg, color } }

// Expiry display. The stored value is an ISO instant.
function isExpiredIso(iso?: string): boolean {
  if (!iso) return false
  const d = new Date(iso)
  return !Number.isNaN(d.getTime()) && d.getTime() < Date.now()
}
/** True while a retired code is still redeemable — the only rotation state with
 *  an action attached (reprint before it lapses), and so the only one that earns
 *  a warning colour. */
function hasLiveRetired(v: Validation): boolean {
  const now = Date.now()
  return (v.retiredCodes ?? []).some(r => new Date(r.overlapUntil).getTime() > now)
}

function shortDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}
function iconBtn(): CSSProperties {
  return { width: 26, height: 26, borderRadius: 6, background: 'transparent', border: '1px solid var(--color-gray-300)', color: 'var(--color-gray-600)', cursor: 'pointer', fontSize: 13, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }
}

// Inline load-failure block — shown in place of the empty state when a fetch
// fails, so a partner never mistakes "couldn't load" for "you have none" (and,
// e.g., mints a duplicate API key). Mirrors the red banner tone used in
// Validate / SignIn; Retry re-runs the loader.
function LoadError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div style={{ background: 'rgba(237,28,36,0.10)', border: '1.5px solid rgba(237,28,36,0.4)', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#b91c1c', marginBottom: 2 }}>Couldn't load</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', lineHeight: 1.5 }}>{message}</div>
      </div>
      <button onClick={onRetry} style={{ padding: '8px 18px', borderRadius: buttonRadius, background: '#7F3F98', color: '#fff', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Retry</button>
    </div>
  )
}

// ── Signage ─────────────────────────────────────────────────────────────────

/**
 * Download a printable card carrying the code.
 *
 * SVG rather than PNG: signage gets printed at whatever size the tenant's
 * printer feels like, and vector survives that. It also keeps this dependency-
 * free — no canvas, no image library — and the file is a few hundred bytes.
 *
 * The card carries the code, the offer name and the business name, and nothing
 * else. Deliberately not the discount terms: those change (an operator re-prices
 * an offer without reissuing the code), and a printed sign that contradicts what
 * the app charges is worse than a sign that just says how to validate.
 */
function downloadCodeCard(v: Validation, businessName: string): void {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  // Shrink the code's type size for long codes so it can never overrun the card.
  const codeSize = v.code.length > 14 ? 44 : v.code.length > 10 ? 56 : 68
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="600" height="380" viewBox="0 0 600 380">
  <rect width="600" height="380" rx="18" fill="#ffffff" stroke="#e5e5e5" stroke-width="2"/>
  <text x="300" y="64" text-anchor="middle" font-family="Montserrat, Avenir, Helvetica, Arial, sans-serif"
        font-size="15" font-weight="700" letter-spacing="2" fill="#7F3F98">VALIDATED PARKING</text>
  <text x="300" y="112" text-anchor="middle" font-family="Montserrat, Avenir, Helvetica, Arial, sans-serif"
        font-size="19" font-weight="600" fill="#231F20">${esc(businessName)}</text>
  <text x="300" y="204" text-anchor="middle" font-family="ui-monospace, SFMono-Regular, Menlo, monospace"
        font-size="${codeSize}" font-weight="700" fill="#231F20">${esc(v.code)}</text>
  ${v.name ? `<text x="300" y="248" text-anchor="middle" font-family="Montserrat, Avenir, Helvetica, Arial, sans-serif"
        font-size="17" font-weight="500" fill="#555">${esc(v.name)}</text>` : ''}
  <text x="300" y="316" text-anchor="middle" font-family="Montserrat, Avenir, Helvetica, Arial, sans-serif"
        font-size="14" fill="#777">Enter this code when you pay for parking</text>
</svg>`
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }))
  const a = document.createElement('a')
  a.href = url
  a.download = `parkify-${v.code.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.svg`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/**
 * What this code's rotation history means for the tenant's printed signage.
 *
 * Only rendered when there IS a retired predecessor. The general "nothing is on
 * a schedule" statement belongs once, at the section level — repeating it on
 * every row would be noise on the overwhelmingly common case.
 */
function RotationNote({ retired }: { retired: NonNullable<Validation['retiredCodes']> }) {
  const now = Date.now()
  // Newest first from the API, so the first entry is the code most likely still
  // on a wall somewhere.
  const live = retired.filter(r => new Date(r.overlapUntil).getTime() > now)
  const dead = retired.filter(r => new Date(r.overlapUntil).getTime() <= now)
  return (
    <div style={{ marginTop: 3, fontSize: 11, fontWeight: 500 }}>
      {live.map(r => (
        <div key={r.code} style={{ color: '#92400e' }}>
          Old code <code style={{ fontFamily: 'monospace', fontWeight: 700 }}>{r.code}</code> still works until {shortDate(r.overlapUntil)} — reprint before then.
        </div>
      ))}
      {dead.map(r => (
        <div key={r.code} style={{ color: 'var(--color-gray-600)' }}>
          <code style={{ fontFamily: 'monospace', fontWeight: 700 }}>{r.code}</code> stopped working {shortDate(r.overlapUntil)}.
        </div>
      ))}
    </div>
  )
}

/**
 * Who hears about a code rotation.
 *
 * Sits with the rotation copy rather than in Branding, because it isn't styling —
 * it's the routing for a message that costs the tenant money to ignore. Blank
 * falls back to the account contact server-side, so the empty state is safe
 * rather than silent.
 *
 * The address is TYPED, never derived. Guessing it from the contact's name plus
 * the tenant's domain invents a mailbox that may not exist, and a bounced
 * rotation notice fails quietly — the tenant finds out when a guest is stuck at
 * the gate holding a dead code.
 */
function RotationContactCard({ session, tenant }: { session: TenantSession; tenant: TenantInfo }) {
  const [email, setEmail] = useState(tenant.rotationEmail ?? '')
  const [base, setBase] = useState(tenant.rotationEmail ?? '')
  const [busy, setBusy] = useState(false)
  const [savedAt, setSavedAt] = useState(0)

  const trimmed = email.trim()
  const dirty = trimmed !== base
  const valid = trimmed === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)

  const save = async () => {
    if (busy || !dirty || !valid) return
    setBusy(true)
    try {
      await updateBranding(session.token, { rotationEmail: trimmed })
      setBase(trimmed)
      setSavedAt(Date.now())
    } catch (e) {
      alert(`Save failed: ${e instanceof Error ? e.message : 'error'}`)
    } finally { setBusy(false) }
  }

  return (
    <div style={{ background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 2 }}>Who to tell if a code changes</div>
      <div style={{ fontSize: 11.5, color: 'var(--color-gray-600)', marginBottom: 10, lineHeight: 1.5 }}>
        Whoever reprints your signage — often not the person on the account.
        Leave blank to use {tenant.designatedContactName
          ? `${tenant.designatedContactName}, your designated contact`
          : tenant.contactEmail || 'your account contact'}.
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder={tenant.contactEmail || 'name@business.com'}
          style={{ ...inputStyle, flex: 1, minWidth: 220, borderColor: valid ? 'var(--color-gray-300)' : 'rgba(237,28,36,0.6)' }}
        />
        <button onClick={() => void save()} disabled={busy || !dirty || !valid} style={{ ...purpleBtn, opacity: busy || !dirty || !valid ? 0.5 : 1 }}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        {savedAt > 0 && !dirty && <span style={{ fontSize: 11, color: '#15803d', fontWeight: 700 }}>Saved</span>}
      </div>
      {!valid && <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 6 }}>That doesn't look like an email address.</div>}
    </div>
  )
}
